// =====================================================
// MARKDOWN UTILITIES - SCOPINGAI
// =====================================================

/**
 * Enhanced markdown to HTML converter with proper styling
 * Handles headings, bold, italic, lists, links, and inline code
 */
export const convertMarkdownToHTML = (markdown: string): string => {
  if (!markdown) return "";

  // Split into lines for better processing
  const lines = markdown.split('\n');
  const processedLines = [];
  let inList = false;
  let listType = '';

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    
    // Process headings (must be at start of line)
    if (line.match(/^#{1,6}\s/)) {
      if (inList) {
        processedLines.push(`</${listType}>`);
        inList = false;
      }
      line = line
        .replace(/^### (.*$)/gim, "<h3 style='margin: 1.5rem 0 1rem 0; font-size: 1.25rem; font-weight: 600; color: #1f2937;'>$1</h3>")
        .replace(/^## (.*$)/gim, "<h2 style='margin: 2rem 0 1rem 0; font-size: 1.5rem; font-weight: 700; color: #111827;'>$1</h2>")
        .replace(/^# (.*$)/gim, "<h1 style='margin: 2rem 0 1.5rem 0; font-size: 2rem; font-weight: 800; color: #111827;'>$1</h1>");
    }
    // Process lists
    else if (line.match(/^[\*\-]\s/) || line.match(/^[0-9]+\.\s/)) {
      const isNumbered = line.match(/^[0-9]+\.\s/);
      const newListType = isNumbered ? 'ol' : 'ul';
      
      if (!inList) {
        processedLines.push(`<${newListType} style='margin: 1rem 0; padding-left: 1.5rem;'>`);
        inList = true;
        listType = newListType;
      } else if (listType !== newListType) {
        processedLines.push(`</${listType}>`);
        processedLines.push(`<${newListType} style='margin: 1rem 0; padding-left: 1.5rem;'>`);
        listType = newListType;
      }
      
      const listContent = line.replace(/^[\*\-]\s/, '').replace(/^[0-9]+\.\s/, '');
      line = `<li style='margin: 0.5rem 0; line-height: 1.6;'>${processInlineFormatting(listContent)}</li>`;
    }
    // Process blockquotes
    if (line.match(/^>\s/)) {
      if (inList) {
        processedLines.push(`</${listType}>`);
        inList = false;
      }
      line = `<blockquote style='border-left: 4px solid #3182ce; margin: 1.5rem 0; padding: 0.5rem 1rem; color: #888; font-style: italic; background: #f8fafc;'>${processInlineFormatting(line.replace(/^>\s*/, ''))}</blockquote>`;
      processedLines.push(line);
      continue;
    }
    // Regular text
    else {
      if (inList && line.trim() === '') {
        // Empty line in list - continue list
        continue;
      } else if (inList) {
        processedLines.push(`</${listType}>`);
        inList = false;
      }
      
      // Process inline formatting
      line = processInlineFormatting(line);
      
      // Add paragraph tags for non-empty lines
      if (line.trim() && !line.match(/^<[h1-6|ul|ol|li]/)) {
        line = `<p style='margin: 1rem 0; line-height: 1.7; color: #374151;'>${line}</p>`;
      }
    }
    
    processedLines.push(line);
  }
  
  // Close any open list
  if (inList) {
    processedLines.push(`</${listType}>`);
  }
  
  return processedLines.join('\n');
};

/**
 * Process inline markdown formatting (bold, italic, links, code)
 */
export const processInlineFormatting = (text: string): string => {
  if (!text) return "";
  
  return text
    // Process bold text
    .replace(/\*\*(.*?)\*\*/g, "<strong style='font-weight: 600;'>$1</strong>")
    // Process italic text
    .replace(/\*(.*?)\*/g, "<em style='font-style: italic;'>$1</em>")
    // Process links
    .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" style="color: #2563eb; text-decoration: underline;">$1</a>')
    // Process inline code
    .replace(/`(.*?)`/g, '<code style="background-color: #f3f4f6; padding: 0.125rem 0.25rem; border-radius: 0.25rem; font-family: monospace; font-size: 0.875em;">$1</code>');
};

/**
 * Simple markdown to plain text converter (removes formatting)
 */
export const markdownToPlainText = (markdown: string): string => {
  if (!markdown) return "";
  
  return markdown
    // Remove headings
    .replace(/^#{1,6}\s+/gm, '')
    // Remove bold and italic
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    // Remove links (keep text)
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    // Remove inline code
    .replace(/`(.*?)`/g, '$1')
    // Remove list markers
    .replace(/^[\*\-]\s+/gm, '')
    .replace(/^[0-9]+\.\s+/gm, '')
    // Clean up extra whitespace
    .replace(/\n\s*\n/g, '\n\n')
    .trim();
};

/**
 * Get markdown formatting help text
 */
export const getMarkdownHelp = (): string => {
  return `Markdown Formatting Guide:

# Heading 1
## Heading 2  
### Heading 3

**Bold text**
*Italic text*

- Bullet point
- Another bullet point

1. Numbered list
2. Another item

[Link text](https://example.com)
\`inline code\``;
};

/**
 * Validate if text contains valid markdown
 */
export const isValidMarkdown = (text: string): boolean => {
  if (!text) return true;
  
  // Check for basic markdown patterns
  const markdownPatterns = [
    /^#{1,6}\s+/m,           // Headings
    /\*\*.*?\*\*/,           // Bold
    /\*.*?\*/,               // Italic
    /^[\*\-]\s+/m,           // Lists
    /^[0-9]+\.\s+/m,         // Numbered lists
    /\[.*?\]\(.*?\)/,        // Links
    /`.*?`/                  // Inline code
  ];
  
  return markdownPatterns.some(pattern => pattern.test(text));
};

/**
 * Count words in markdown text (excluding formatting)
 */
export const countWordsInMarkdown = (markdown: string): number => {
  const plainText = markdownToPlainText(markdown);
  return plainText.split(/\s+/).filter(word => word.length > 0).length;
};

/**
 * Extract headings from markdown for table of contents
 */
export const extractHeadings = (markdown: string): Array<{level: number, text: string, id: string}> => {
  if (!markdown) return [];
  
  const headings = [];
  const lines = markdown.split('\n');
  
  for (const line of lines) {
    const match = line.match(/^(#{1,6})\s+(.+)$/);
    if (match) {
      const level = match[1].length;
      const text = match[2].trim();
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      
      headings.push({ level, text, id });
    }
  }
  
  return headings;
};
